// Pure logic of ServiceMultiSelect.vue (no Vue / store / API access, so it is covered by unit tests).
//
// The selection (`f` in the component) is what vue-multiselect shows: an option object, an array of them (multiple),
// or null. In onlyId mode v-model holds only the id (option[valueId]) instead of the whole option.

// option with the given id (compared as strings)
export const findOption = (options, id, valueId) => {
  return options.find(o => o[valueId] != null && o[valueId].toString() === id.toString())
}

/**
 * What to emit as v-model (input) for the selection: { emit: false } - nothing.
 * onlyId: the id of the selected option ('' when cleared). Selections there are always option objects -
 * a raw id came from the parent (its option is not loaded) and is not emitted back, otherwise the parent value
 * would be overwritten with undefined / ''
 */
export const emittedValue = (selection, { onlyId, valueId }) => {
  if(!onlyId) return { emit: true, value: selection }
  if(selection !== null && typeof selection !== 'object') return { emit: false }
  return { emit: true, value: selection ? selection[valueId] : '' }
}

// selection for a new v-model value from the parent. onlyId: the option with that id,
// or the raw id when the option is not loaded (kept, not emitted - see emittedValue)
export const selectionFromValue = (value, options, { onlyId, valueId }) => {
  if(!onlyId) return value
  if(value == null) return null
  return findOption(options, value, valueId) || value
}

// selection and options when the component is mounted. onlyId: the option with that id among prepopulated
// options (undefined when not found - the placeholder is shown)
export const initialState = (value, prepopulatedOptions, { onlyId, valueId }) => {
  if(!prepopulatedOptions) return { selection: value, options: null }
  let selection = value
  if(onlyId && value) selection = findOption(prepopulatedOptions, value, valueId)
  return { selection, options: prepopulatedOptions }
}

// search results without ignored ids (results without id are kept)
export const withoutIgnored = (results, ignoreIds) => {
  if(!ignoreIds || !ignoreIds.length) return results
  return results.filter(x => x && (!x.id || !ignoreIds.includes(x.id)))
}

// text shown in read-only mode
export const selectionText = (selection, title) => {
  if(!selection || selection.length === 0) return '-'
  if(Array.isArray(selection)) {
    return selection.map(o => o[title]).join(', ')
  }
  return selection[title]
}

// selected options shown as tags below the field (resultsBelow)
export const belowTags = (selection, resultsBelow) => {
  if(!resultsBelow) return []
  return Array.isArray(selection) ? selection : (selection ? [selection] : [])
}

// the last selected tag can not be removed when an empty selection is not allowed
export const isLastTagLocked = (selection, allowEmpty) => {
  return !allowEmpty && Array.isArray(selection) && selection.length <= 1
}

// selection without the removed tag (tags below the field); single selection becomes null
export const withoutTag = (selection, option, title) => {
  if(Array.isArray(selection)) {
    return selection.filter(o => o[title] !== option[title])
  }
  return null
}

// double click on a tag of vue-multiselect: index of the tag among selected ones, -1 when not on a tag text
export const clickedTagIndex = (target) => {
  let node = target
  if(node && node.parentNode && node.parentNode.classList.contains('multiselect__tag')) {
    node = node.parentNode
    return Array.from(node.parentNode.children).indexOf(node)
  }
  return -1
}
